import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  useTheme,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { CatalogProductStore } from "../../../stores/CatalogProductStore";
import { ProductActivityLog } from "../../../../../models/CatalogProducts/ProductActivityLog";
import { getApiErrorMessage } from "../../../../../models/CatalogProducts/types";
import {
  getModernPageColors,
  modernDrawerHeaderSx,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";

interface ProductActivityLogsDrawerProps {
  open: boolean;
  onClose: () => void;
  productId: number | null;
  catalogProductStore: CatalogProductStore;
}

/** Above AppBar (`theme.zIndex.drawer + 1`). */
const ACTIVITY_LOGS_DRAWER_Z_OFFSET = 2000;

const displayChangeValue = (
  value: string | number | boolean | null,
): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const groupLogsByDate = (
  logs: ProductActivityLog[],
): Record<string, ProductActivityLog[]> => {
  const grouped: Record<string, ProductActivityLog[]> = {};
  logs.forEach((log) => {
    const dateKey = formatDateWithoutTime(log.created_date, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(log);
  });
  return grouped;
};

const getInitials = (name: string | null): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(" ").filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return trimmed[0].toUpperCase();
};

const AVATAR_COLORS = [
  "#1976d2",
  "#388e3c",
  "#d32f2f",
  "#7b1fa2",
  "#f57c00",
  "#0288d1",
  "#689f38",
  "#e64a19",
];

const getAvatarColor = (name: string | null): string => {
  const trimmed = name?.trim() || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    hash = trimmed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/** A generic "these were added, these were removed" result, labeled for display. */
interface LabelDiff {
  added: string[];
  removed: string[];
}

const extractListArray = (
  source: unknown,
  arrayKey: string,
): Record<string, unknown>[] => {
  // Some actions (e.g. `update_variants`) store the list directly as before/after.
  if (Array.isArray(source)) return source as Record<string, unknown>[];
  if (!source || typeof source !== "object") return [];
  // Others (e.g. a full product `update`) nest it under a named key.
  const candidate = (source as Record<string, unknown>)[arrayKey];
  return Array.isArray(candidate) ? (candidate as Record<string, unknown>[]) : [];
};

const variantKey = (variant: Record<string, unknown>): string => {
  const sku = variant.sku;
  if (typeof sku === "string" && sku.trim()) return `sku:${sku.trim().toLowerCase()}`;
  const id = variant.id;
  if (id !== undefined && id !== null && id !== "") return `id:${id}`;
  return JSON.stringify(variant);
};

const variantLabel = (variant: Record<string, unknown>): string => {
  const title = variant.title || variant.name || variant.option_name;
  const sku = variant.sku;
  if (title && sku) return `${title} (${sku})`;
  if (title) return String(title);
  if (sku) return String(sku);
  return "Variant";
};

/**
 * `changes.before`/`changes.after` carry the full product snapshot for every log
 * (not just `update_variants`), so diff `variants[]` regardless of `action` —
 * it only renders when the sets actually differ.
 */
const getVariantDiff = (log: ProductActivityLog): LabelDiff | null => {
  const before = extractListArray(log.changes?.before, "variants");
  const after = extractListArray(log.changes?.after, "variants");
  if (!before.length && !after.length) return null;

  const beforeMap = new Map(before.map((v) => [variantKey(v), v]));
  const afterMap = new Map(after.map((v) => [variantKey(v), v]));

  const added: string[] = [];
  afterMap.forEach((variant, key) => {
    if (!beforeMap.has(key)) added.push(variantLabel(variant));
  });

  const removed: string[] = [];
  beforeMap.forEach((variant, key) => {
    if (!afterMap.has(key)) removed.push(variantLabel(variant));
  });

  if (!added.length && !removed.length) return null;
  return { added, removed };
};

/**
 * Generic diff for "named list" fields (categories, vendors, printing types) —
 * each item is matched by `idKey` and displayed using `nameKey`.
 */
const getNamedListDiff = (
  log: ProductActivityLog,
  arrayKey: string,
  idKey: string,
  nameKey: string,
): LabelDiff | null => {
  const before = extractListArray(log.changes?.before, arrayKey);
  const after = extractListArray(log.changes?.after, arrayKey);
  if (!before.length && !after.length) return null;

  const keyOf = (item: Record<string, unknown>): string => {
    const id = item[idKey];
    if (id !== undefined && id !== null && id !== "") return `id:${id}`;
    return JSON.stringify(item);
  };
  const labelOf = (item: Record<string, unknown>): string => {
    const name = item[nameKey];
    return name ? String(name) : "Item";
  };

  const beforeMap = new Map(before.map((item) => [keyOf(item), item]));
  const afterMap = new Map(after.map((item) => [keyOf(item), item]));

  const added: string[] = [];
  afterMap.forEach((item, key) => {
    if (!beforeMap.has(key)) added.push(labelOf(item));
  });

  const removed: string[] = [];
  beforeMap.forEach((item, key) => {
    if (!afterMap.has(key)) removed.push(labelOf(item));
  });

  if (!added.length && !removed.length) return null;
  return { added, removed };
};

interface ImageEntry {
  key: string;
  url: string;
}

interface ImageDiffResult {
  added: ImageEntry[];
  removed: ImageEntry[];
}

const extractImagesArray = (source: unknown): Record<string, unknown>[] => {
  const images = extractListArray(source, "images");
  if (images.length) return images;
  return extractListArray(source, "image_urls");
};

const imageKey = (image: Record<string, unknown>): string => {
  const url = image.image_url;
  if (typeof url === "string" && url.trim()) return url.trim();
  const id = image.id;
  if (id !== undefined && id !== null && id !== "") return `id:${id}`;
  return JSON.stringify(image);
};

/** Diffs before/after image lists (product images or a single variant's images). */
const getImageDiff = (log: ProductActivityLog): ImageDiffResult | null => {
  const before = extractImagesArray(log.changes?.before);
  const after = extractImagesArray(log.changes?.after);
  if (!before.length && !after.length) return null;

  const toEntry = (image: Record<string, unknown>): ImageEntry | null => {
    const url = image.image_url;
    if (typeof url !== "string" || !url.trim()) return null;
    return { key: imageKey(image), url: url.trim() };
  };

  const beforeMap = new Map(
    before.map((img) => [imageKey(img), img] as const),
  );
  const afterMap = new Map(after.map((img) => [imageKey(img), img] as const));

  const added: ImageEntry[] = [];
  afterMap.forEach((image, key) => {
    if (!beforeMap.has(key)) {
      const entry = toEntry(image);
      if (entry) added.push(entry);
    }
  });

  const removed: ImageEntry[] = [];
  beforeMap.forEach((image, key) => {
    if (!afterMap.has(key)) {
      const entry = toEntry(image);
      if (entry) removed.push(entry);
    }
  });

  if (!added.length && !removed.length) return null;
  return { added, removed };
};

/** Generic array-diff summary fields the backend emits when it can't diff field-by-field. */
const GENERIC_COUNT_FIELDS = new Set([
  "before",
  "after",
  "images",
  "variants",
  "variant_images",
  "sku_variants",
  "categories",
  "vendors",
  "printing_types",
]);

/** Renders "<Entity> added/removed" chip rows for a {@link LabelDiff} — shared by variants, categories, vendors, printing types. */
const LabelDiffSection: React.FC<{
  diff: LabelDiff | null;
  entityLabel: string;
  colors: ReturnType<typeof getModernPageColors>;
  keyPrefix: string;
}> = ({ diff, entityLabel, colors, keyPrefix }) => {
  if (!diff) return null;

  return (
    <>
      {diff.added.length ? (
        <Box>
          <CustomTypography
            variant="body2"
            sx={{ fontWeight: 600, fontSize: "0.72rem", color: colors.success, mb: 0.5 }}
          >
            {entityLabel} added ({diff.added.length})
          </CustomTypography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {diff.added.map((label, index) => (
              <Chip
                key={`${keyPrefix}-added-${index}`}
                label={label}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  bgcolor: `${colors.success}1A`,
                  color: colors.success,
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      {diff.removed.length ? (
        <Box>
          <CustomTypography
            variant="body2"
            sx={{ fontWeight: 600, fontSize: "0.72rem", color: colors.error, mb: 0.5 }}
          >
            {entityLabel} removed ({diff.removed.length})
          </CustomTypography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {diff.removed.map((label, index) => (
              <Chip
                key={`${keyPrefix}-removed-${index}`}
                label={label}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  bgcolor: `${colors.error}1A`,
                  color: colors.error,
                  textDecoration: "line-through",
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  );
};

const ActivityLogEntry: React.FC<{ log: ProductActivityLog }> = ({ log }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const [expanded, setExpanded] = useState(false);
  const variantDiff = getVariantDiff(log);
  const imageDiff = getImageDiff(log);
  const categoryDiff = getNamedListDiff(
    log,
    "categories",
    "category_id",
    "category_name",
  );
  const vendorDiff = getNamedListDiff(log, "vendors", "vendor_id", "vendor_name");
  const printingTypeDiff = getNamedListDiff(
    log,
    "printing_types",
    "printing_type_id",
    "printing_type_name",
  );
  const hasStructuredDiff =
    Boolean(variantDiff) ||
    Boolean(imageDiff) ||
    Boolean(categoryDiff) ||
    Boolean(vendorDiff) ||
    Boolean(printingTypeDiff);
  const visibleChangeDetails = (log.change_details || []).filter(
    (change) => !hasStructuredDiff || !GENERIC_COUNT_FIELDS.has(change.field),
  );
  const hasChangeDetails = Boolean(visibleChangeDetails.length) || hasStructuredDiff;

  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#fff",
          backgroundColor: getAvatarColor(log.admin_name),
        }}
      >
        {getInitials(log.admin_name)}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          p: 1.5,
          bgcolor: colors.surface,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.75,
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={log.action_label}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "0.7rem" }}
            />
            {log.entity_type_label && (
              <Chip
                label={log.entity_type_label}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  color: colors.subtitle,
                  borderColor: colors.border,
                }}
              />
            )}
          </Box>
          <CustomTypography
            variant="body2"
            sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
          >
            {formatDate(log.created_date)}
          </CustomTypography>
        </Box>

        <CustomTypography
          variant="body2"
          sx={{ color: colors.title, lineHeight: 1.5, wordBreak: "break-word" }}
        >
          {log.summary}
        </CustomTypography>

        {hasChangeDetails && (
          <>
            <Box
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 1,
                cursor: "pointer",
                color: colors.accent,
                width: "fit-content",
              }}
            >
              <CustomTypography
                variant="body2"
                sx={{ fontSize: "0.72rem", fontWeight: 600, color: "inherit" }}
              >
                {expanded ? "Hide changes" : "View changes"}
              </CustomTypography>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 1.5,
                  bgcolor: colors.surfaceSoft,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {Boolean(visibleChangeDetails.length) && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {visibleChangeDetails.map((change, index) => (
                      <Box
                        key={`${log.id}-${change.field}-${index}`}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          fontSize: "0.72rem",
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          sx={{ fontWeight: 600, color: colors.title, fontSize: "0.72rem" }}
                        >
                          {change.label}:
                        </CustomTypography>
                        <CustomTypography
                          variant="body2"
                          sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
                        >
                          {displayChangeValue(change.from)} → {displayChangeValue(change.to)}
                        </CustomTypography>
                      </Box>
                    ))}
                  </Box>
                )}

                <LabelDiffSection
                  diff={variantDiff}
                  entityLabel="Variants"
                  colors={colors}
                  keyPrefix={`variant-${log.id}`}
                />
                <LabelDiffSection
                  diff={categoryDiff}
                  entityLabel="Categories"
                  colors={colors}
                  keyPrefix={`category-${log.id}`}
                />
                <LabelDiffSection
                  diff={vendorDiff}
                  entityLabel="Vendors"
                  colors={colors}
                  keyPrefix={`vendor-${log.id}`}
                />
                <LabelDiffSection
                  diff={printingTypeDiff}
                  entityLabel="Printing types"
                  colors={colors}
                  keyPrefix={`printing-type-${log.id}`}
                />

                {imageDiff?.added.length ? (
                  <Box>
                    <CustomTypography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        color: colors.success,
                        mb: 0.5,
                      }}
                    >
                      Added ({imageDiff.added.length})
                    </CustomTypography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {imageDiff.added.map((image) => (
                        <Box
                          key={`image-added-${log.id}-${image.key}`}
                          component="a"
                          href={image.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "block",
                            width: 44,
                            height: 44,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            border: `2px solid ${colors.success}`,
                          }}
                        >
                          <Box
                            component="img"
                            src={image.url}
                            alt="Added image"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {imageDiff?.removed.length ? (
                  <Box>
                    <CustomTypography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        color: colors.error,
                        mb: 0.5,
                      }}
                    >
                      Removed ({imageDiff.removed.length})
                    </CustomTypography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {imageDiff.removed.map((image) => (
                        <Box
                          key={`image-removed-${log.id}-${image.key}`}
                          component="a"
                          href={image.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "block",
                            width: 44,
                            height: 44,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            border: `2px solid ${colors.error}`,
                            opacity: 0.6,
                          }}
                        >
                          <Box
                            component="img"
                            src={image.url}
                            alt="Removed image"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                              filter: "grayscale(60%)",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
};

const ProductActivityLogsDrawer: React.FC<ProductActivityLogsDrawerProps> = ({
  open,
  onClose,
  productId,
  catalogProductStore,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const { showError } = useNotification();

  useEffect(() => {
    if (!open || !productId) return;
    catalogProductStore.getActivityLogs(productId, 1).catch((error: any) => {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to fetch activity logs."),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    if (!productId) return;
    catalogProductStore
      .getActivityLogs(productId, page)
      .catch((error: any) => {
        showError(
          "Error",
          getApiErrorMessage(error, "Failed to fetch activity logs."),
        );
      });
  };

  const groupedLogs = groupLogsByDate(catalogProductStore.activityLogs);
  const sortedDates = Object.keys(groupedLogs).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );
  const productName = catalogProductStore.activityLogs[0]?.product_name;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460, md: 520 },
          maxWidth: { xs: "100vw", sm: 520 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box minWidth={0}>
            <CustomTypography variant="heading5" sx={{ color: colors.title }}>
              Activity Logs
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {productName || (productId ? `Product #${productId}` : "Product history")}
            </CustomTypography>
          </Box>
        </Box>

        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {catalogProductStore.activityLogsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : catalogProductStore.activityLogs.length === 0 ? (
          <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 240 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: 44, opacity: 0.5 }} />
              <CustomTypography variant="body1">
                No activity recorded for this product yet.
              </CustomTypography>
            </Box>
          </Box>
        ) : (
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box sx={{ height: "1px", flex: 1, backgroundColor: colors.border }} />
                <CustomTypography
                  variant="body2"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    backgroundColor: colors.headerBg,
                    borderRadius: "12px",
                    color: colors.subtitle,
                  }}
                >
                  {date}
                </CustomTypography>
                <Box sx={{ height: "1px", flex: 1, backgroundColor: colors.border }} />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {groupedLogs[date].map((log) => (
                  <ActivityLogEntry key={log.id} log={log} />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {catalogProductStore.activityLogsTotalCount > 0 && (
        <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
          <AppTablePagenation
            fontSize="0.75rem"
            pageNo={catalogProductStore.activityLogsPageNo}
            limit={catalogProductStore.activityLogsLimit}
            totalCount={catalogProductStore.activityLogsTotalCount}
            showRowsPerPageOptions={false}
            onPageChange={handlePageChange}
            handleRowsChange={() => {}}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default observer(ProductActivityLogsDrawer);
